import { FC, useMemo } from 'react';
// Tag has no Base equivalent — keep antd. Table stays as antd for this inner
// read-only diff (no pagination / filters / per-column settings needed).
import { Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EmptyView } from 'components/Base/EmptyView';
import { EIconNames } from 'components/Base/Icon';
import { TMergeHistoryEntryUI } from 'data/types/duplicateManagement.types';

// Shape produced by MergeExecutionService (BE GROW-3959). The persisted JSON
// is keyed by the secondary record id; each entry is an array of per-field
// resolutions. `cleared_fields_user_{id}` carries the unique fields wiped on
// the archived secondary (separate from field_resolutions).
type TFieldResolution = {
  field: string;
  primary_value: unknown;
  applied_value: unknown;
  winner: 'PRIMARY' | 'SECONDARY';
  changed: boolean;
};

type TFieldDeltasV2 = {
  version: 2;
  field_resolutions?: Record<string, TFieldResolution[]>;
  [key: `cleared_fields_user_${string}`]: Record<string, unknown> | undefined;
};

type TFieldDeltasViewProps = {
  entry: TMergeHistoryEntryUI;
};

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const FieldDeltasView: FC<TFieldDeltasViewProps> = ({ entry }) => {
  const resolutions = useMemo(() => {
    const deltas = entry.fieldDeltas as TFieldDeltasV2 | null;
    if (!deltas?.field_resolutions) return [];
    return Object.entries(deltas.field_resolutions).flatMap(
      ([secondaryId, rows]) =>
        rows.map((r, idx) => ({
          key: `${secondaryId}-${r.field}-${idx}`,
          secondaryId,
          ...r,
        })),
    );
  }, [entry.fieldDeltas]);

  const columns: ColumnsType<(typeof resolutions)[number]> = [
    {
      title: 'Field',
      dataIndex: 'field',
      key: 'field',
      width: 180,
    },
    {
      title: 'Before (primary)',
      key: 'primary_value',
      render: (_v, row) => formatValue(row.primary_value),
    },
    {
      title: 'After',
      key: 'applied_value',
      render: (_v, row) => formatValue(row.applied_value),
    },
    {
      title: 'Winner',
      key: 'winner',
      width: 110,
      render: (_v, row) => (
        <Tag color={row.winner === 'PRIMARY' ? 'green' : 'orange'}>
          {row.winner}
        </Tag>
      ),
    },
    {
      title: 'Changed',
      key: 'changed',
      width: 100,
      render: (_v, row) =>
        row.changed ? <Tag color="blue">Yes</Tag> : <Tag>No</Tag>,
    },
  ];

  return (
    <div>
      {resolutions.length === 0 ? (
        <EmptyView
          alt="no field changes"
          emptyText="No field-level changes recorded for this merge."
          icon={EIconNames.EMPTY_DATA}
        />
      ) : (
        <Table
          size="small"
          rowKey="key"
          columns={columns}
          dataSource={resolutions}
          pagination={false}
        />
      )}
    </div>
  );
};

export default FieldDeltasView;
